
import { SimulationConfig, Vector2D, Ball } from '../types';

export class CameraService {
  private currentPos: Vector2D = { x: 0, y: 0 };
  private targetPos: Vector2D = { x: 0, y: 0 };
  private initialized: boolean = false;

  public reset() {
    this.currentPos = { x: 0, y: 0 };
    this.targetPos = { x: 0, y: 0 };
    this.initialized = true;
  }

  public update(
    canvasWidth: number,
    canvasHeight: number,
    balls: Ball[],
    config: SimulationConfig,
    shakeAmount: number
  ): { x: number, y: number, scale: number } {
    if (!config.cameraFollow || balls.length === 0) {
      // If disabled or no targets, drift back to origin smoothly
      if (!config.cameraFollow) {
          this.targetPos.x = 0;
          this.targetPos.y = 0;
      }
    } else {
      // Track the first ball
      const targetBall = balls[0];
      
      // 1. Calculate ideal position based on ball position
      let idealX = targetBall.pos.x;
      let idealY = targetBall.pos.y;

      // 2. Add Lookahead based on velocity
      if (config.cameraLookahead > 0) {
        idealX += targetBall.vel.x * config.cameraLookahead * 5;
        idealY += targetBall.vel.y * config.cameraLookahead * 5;
      }

      // 3. Apply Deadzone
      const dx = idealX - this.currentPos.x;
      const dy = idealY - this.currentPos.y;
      const dist = Math.sqrt(dx*dx + dy*dy);
      
      if (dist > config.cameraDeadzone) {
         const angle = Math.atan2(dy, dx);
         const moveDist = dist - config.cameraDeadzone;
         this.targetPos.x = this.currentPos.x + Math.cos(angle) * moveDist;
         this.targetPos.y = this.currentPos.y + Math.sin(angle) * moveDist;
      }
    }

    if (!this.initialized) {
        this.currentPos = { ...this.targetPos };
        this.initialized = true;
    }

    // 4. Smooth Interpolation (Lerp)
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const alpha = Math.max(0.001, Math.min(1.0, config.cameraSmoothness));

    this.currentPos.x = lerp(this.currentPos.x, this.targetPos.x, alpha);
    this.currentPos.y = lerp(this.currentPos.y, this.targetPos.y, alpha);

    // Calculate final draw offset
    // We want currentPos to be at the center of the screen.
    // So we translate the world by (-CurrentPos)
    // The SimulationRenderer will add the screen center (centerX, centerY)
    let offsetX = -this.currentPos.x;
    let offsetY = -this.currentPos.y;

    // Apply Shake
    if (shakeAmount > 0) {
        offsetX += (Math.random() - 0.5) * shakeAmount;
        offsetY += (Math.random() - 0.5) * shakeAmount;
    }

    return {
        x: offsetX,
        y: offsetY,
        scale: config.cameraZoom || 1.0
    };
  }
}

export const CAMERA = new CameraService();
